/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'
import { Modal } from '@components/molecules'
import { mono } from '@components/systems/monolith/monolith.tokens'

import { CartQuote, PriceChangesType } from '@utils'

interface PropsType {
	show: boolean
	changes: PriceChangesType
	/** 체크된(구매 의사 있는) 오른 줄의 시즌상품 ID 를 돌려준다. 내린 줄은 항상 포함 */
	onConfirm: (keepIds: string[]) => void
	onCancel: () => void
}

/**
 * 값이 바뀌었을 때 묻는 모달 (사용자 결정 2026-09-16, judahsWeb/CHECKOUT_PLAN.md §13).
 *
 * - 내렸을 때: 알리기만 하고 넘어간다 — 좋은 일이다.
 * - 올랐을 때: 어떤 제품이 얼마에서 얼마로 올랐는지 줄마다 적고, **그래도 사겠는가**를 묻는다.
 *   줄마다 체크박스. 기본은 전부 체크(구매 의사). 체크를 풀면 그 제품은 빠진다.
 *   체크된 것만 결제 화면으로 간다. 하나도 없으면 진행 버튼이 잠긴다.
 */
export const PriceChangeModal = (props: PropsType) => {
	const changes: PriceChangesType = props.changes ?? {
		currency: 'USD',
		increased: [],
		decreased: [],
	}
	const [keep, setKeep] = React.useState<Set<string>>(new Set())

	React.useEffect(() => {
		if (props.show) {
			setKeep(new Set(changes.increased.map(aChange => aChange.seasonProductId)))
		}
	}, [props.show, changes])

	const money = (value: number): string => CartQuote.money(changes.currency, value)
	const hasIncrease = changes.increased.length > 0
	const title = hasIncrease ? 'The price went up' : 'The price went down'

	const toggle = (id: string) => {
		setKeep(prev => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	return (
		<Modal
			target="priceChanged"
			title={title}
			show={props.show}
			showSubmit
			submitTitle={hasIncrease ? 'Buy the checked items' : 'Continue'}
			// 인상분이 있는데 아무것도 안 골랐으면 제출을 잠근다
			isSubmitDisabled={hasIncrease && keep.size === 0}
			// 제출이 닫기(onCloseModal)를 함께 부르지 않게 — 부모가 show 를 내려 닫는다
			isDisableDisimissionOnSubmit
			closeTitle={hasIncrease ? 'Back to cart' : 'Close'}
			closeStatus="inherit"
			isTitleDanger={hasIncrease}
			onSubmit={() => props.onConfirm(Array.from(keep))}
			onCloseModal={props.onCancel}
		>
			<View css={style}>
				{changes.decreased.length > 0 && (
					<View className="mnPcBlock">
						<Text category="p2">These prices went down since you added them.</Text>
						<ul className="mnPcList">
							{changes.decreased.map(aChange => (
								<li key={aChange.seasonProductId} className="mnPcRow">
									<span className="mnPcName">
										{aChange.productName}
										{aChange.optionName ? ` · ${aChange.optionName}` : ''}
										{aChange.qty > 1 ? ` ×${aChange.qty}` : ''}
									</span>
									<span className="mnPcPrice">
										<s>{money(aChange.from)}</s> → <b>{money(aChange.to)}</b>
									</span>
								</li>
							))}
						</ul>
					</View>
				)}

				{hasIncrease && (
					<View className="mnPcBlock">
						<Text category="p2">These prices went up since you added them.</Text>
						<Text category="p2">
							Do you still want to buy the items below? Checked items go to checkout.
						</Text>
						<ul className="mnPcList">
							{changes.increased.map(aChange => {
								const id = aChange.seasonProductId
								const checked = keep.has(id)
								return (
									<li key={id} className="mnPcRow">
										<label className="mnPcLabel">
											<input
												type="checkbox"
												className="mnPcCheck"
												checked={checked}
												onChange={() => toggle(id)}
											/>
											<span className={`mnPcName ${checked ? '' : 'off'}`}>
												{aChange.productName}
												{aChange.optionName ? ` · ${aChange.optionName}` : ''}
												{aChange.qty > 1 ? ` ×${aChange.qty}` : ''}
											</span>
										</label>
										<span className={`mnPcPrice ${checked ? '' : 'off'}`}>
											<s>{money(aChange.from)}</s> → <b>{money(aChange.to)}</b>
										</span>
									</li>
								)
							})}
						</ul>
						{keep.size === 0 && (
							<Text category="p2">Nothing is checked. The cart stays as it is.</Text>
						)}
					</View>
				)}
			</View>
		</Modal>
	)
}

const style = css({
	width: '32rem',
	maxWidth: '80vw',
	display: 'flex',
	flexDirection: 'column',
	gap: 16,

	'.mnPcBlock': {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},

	'.mnPcList': {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		display: 'flex',
		flexDirection: 'column',
		borderTop: `1px solid ${mono.line}`,
	},

	'.mnPcRow': {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: 16,
		padding: '10px 0',
		borderBottom: `1px solid ${mono.line}`,
		fontSize: 14,
		color: mono.ink,
	},

	'.mnPcLabel': {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		cursor: 'pointer',
	},

	'.mnPcCheck': {
		width: 16,
		height: 16,
		accentColor: mono.ink,
	},

	'.mnPcName.off, .mnPcPrice.off': {
		color: mono.dim,
		textDecoration: 'line-through',
	},

	'.mnPcPrice': {
		fontVariantNumeric: 'tabular-nums',
		whiteSpace: 'nowrap',

		s: { color: mono.dim, marginRight: 4 },
		b: { fontWeight: 500 },
	},
})
